import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, GraduationCap, MapPin, Briefcase, ArrowRight, RotateCcw, CheckCircle2 } from "lucide-react";

const STORAGE_KEY = "hireds_profile_v1";

const QUALS = [
  { v: "10th", label: "10th pass", hint: "RRB Group D, SSC GD, MTS" },
  { v: "12th", label: "12th pass", hint: "SSC CHSL, GD, Defense" },
  { v: "Graduate", label: "Graduate (any degree)", hint: "SSC CGL, IBPS PO, RRB NTPC" },
  { v: "Engineering", label: "Engineering / Diploma / ITI", hint: "RRB ALP, JE, PSU posts" },
  { v: "Post-graduate", label: "Post-graduate", hint: "UPSC, KVS PGT, RBI Grade B" },
];

const STATES = [
  "Uttar Pradesh", "Bihar", "Maharashtra", "Madhya Pradesh", "Rajasthan",
  "Delhi", "Karnataka", "Tamil Nadu", "West Bengal", "Gujarat",
  "Andhra Pradesh", "Telangana", "Kerala", "Punjab", "Haryana", "Odisha",
];

const SECTORS = [
  { v: "railway", label: "Railway (RRB)", guide: "rrb-ntpc-application-form-step-by-step", post: "rrb-loco-pilot" },
  { v: "ssc", label: "SSC (CGL/CHSL/MTS)", guide: "ssc-cgl-application-form-2026-step-by-step", post: "ssc-cgl-inspector" },
  { v: "banking", label: "Banking (IBPS/SBI/RBI)", guide: "ibps-po-application-form-step-by-step", post: "ibps-po" },
  { v: "upsc", label: "UPSC (CSE/CDS/NDA)", guide: "upsc-cse-application-form-step-by-step", post: "upsc-ias" },
  { v: "defense", label: "Defense (Army/Navy/Air Force)", guide: "ssc-gd-application-form-step-by-step", post: "ssc-gd-constable" },
  { v: "teaching", label: "Teaching (KVS/NVS)", guide: "kvs-tgt-pgt-application-form-step-by-step", post: "kvs-prt" },
  { v: "state-govt", label: "State Govt (PSC/Police/Board)", guide: null, post: null },
  { v: "psu", label: "PSU (ONGC/NTPC/IOCL)", guide: null, post: null },
  { v: "", label: "Any sector — show me everything", guide: null, post: null },
];

function loadProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveProfile(p) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch {}
}

function clearProfile() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function buildJobsUrl({ qualification, state, department }) {
  const params = new URLSearchParams();
  if (qualification) params.set("qualification", qualification);
  if (state) params.set("state", state);
  if (department) params.set("department", department);
  const qs = params.toString();
  return qs ? `/all-india-jobs?${qs}` : "/all-india-jobs";
}

export default function MatchProfileWizard() {
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState({ qualification: "", state: "", department: "" });

  useEffect(() => {
    setProfile(loadProfile());
  }, []);

  const sector = useMemo(
    () => SECTORS.find((s) => s.v === (profile?.department ?? draft.department)) || null,
    [profile, draft.department],
  );

  const start = () => {
    setDraft({ qualification: profile?.qualification || "", state: profile?.state || "", department: profile?.department ?? "" });
    setStep(1);
    setOpen(true);
  };

  const finish = (d) => {
    saveProfile(d);
    setProfile(d);
    setOpen(false);
  };

  const reset = () => {
    clearProfile();
    setProfile(null);
  };

  // ============== Result Card (after profile is saved) ==============
  if (profile && !open) {
    const jobsUrl = buildJobsUrl(profile);
    const matchedSector = SECTORS.find((s) => s.v === profile.department);
    return (
      <section
        data-testid="profile-result-card"
        className="max-w-3xl mx-auto mt-8 bg-white border border-orange-200 rounded-2xl p-5 md:p-7 shadow-sm text-left"
      >
        <div className="flex items-start gap-3 mb-3">
          <span className="inline-grid place-items-center w-10 h-10 rounded-md bg-orange-600 text-white shrink-0">
            <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Your profile match</p>
            <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 leading-snug" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              Hand-picked for {profile.qualification} · {profile.state || "All India"}{matchedSector?.label ? ` · ${matchedSector.label.split(" (")[0]}` : ""}
            </h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <Link
            to={jobsUrl}
            data-testid="profile-cta-jobs"
            className="flex flex-col justify-between p-4 rounded-xl border border-slate-200 bg-gradient-to-br from-orange-50 to-amber-50 hover:border-orange-400 transition group"
          >
            <span className="text-xs font-bold uppercase tracking-wider text-orange-700">Step 1</span>
            <span className="text-sm font-extrabold text-slate-900 mt-1 leading-snug">See matching live vacancies</span>
            <span className="text-xs text-slate-600 mt-2 inline-flex items-center gap-1">Browse filtered jobs <ArrowRight className="w-3 h-3" /></span>
          </Link>
          <Link
            to="/tools/salary-calculator"
            data-testid="profile-cta-salary"
            className="flex flex-col justify-between p-4 rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-green-50 hover:border-green-400 transition group"
          >
            <span className="text-xs font-bold uppercase tracking-wider text-green-700">Step 2</span>
            <span className="text-sm font-extrabold text-slate-900 mt-1 leading-snug">Calculate your in-hand salary</span>
            <span className="text-xs text-slate-600 mt-2 inline-flex items-center gap-1">7th CPC + HRA + DA <ArrowRight className="w-3 h-3" /></span>
          </Link>
          <Link
            to={matchedSector?.guide ? `/guides/${matchedSector.guide}` : "/guides"}
            data-testid="profile-cta-guide"
            className="flex flex-col justify-between p-4 rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50 to-sky-50 hover:border-blue-400 transition group"
          >
            <span className="text-xs font-bold uppercase tracking-wider text-blue-700">Step 3</span>
            <span className="text-sm font-extrabold text-slate-900 mt-1 leading-snug">Form-fill guide for {matchedSector?.label?.split(" (")[0] || "your exam"}</span>
            <span className="text-xs text-slate-600 mt-2 inline-flex items-center gap-1">Avoid rejection mistakes <ArrowRight className="w-3 h-3" /></span>
          </Link>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
          <button type="button" onClick={start} data-testid="profile-edit" className="text-orange-700 font-semibold hover:underline">
            Edit my profile
          </button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={reset} data-testid="profile-reset" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </section>
    );
  }

  // ============== Trigger CTA (no profile yet) ==============
  if (!open) {
    return (
      <button
        type="button"
        onClick={start}
        data-testid="profile-wizard-open"
        className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-orange-300 text-slate-900 text-sm md:text-base font-bold hover:border-orange-500 hover:bg-orange-50 transition shadow-sm"
      >
        <Sparkles className="w-4 h-4 text-orange-600" aria-hidden="true" />
        Match My Profile <span className="text-xs font-normal text-slate-600 ml-1 hidden sm:inline">· 3 questions, 20 seconds</span>
      </button>
    );
  }

  // ============== Wizard (modal) ==============
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      data-testid="profile-wizard-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-h2"
    >
      <div className="bg-white w-full max-w-lg rounded-2xl p-5 sm:p-7 shadow-xl text-left animate-in slide-in-from-bottom-4 duration-200">
        <div className="flex items-start justify-between mb-1">
          <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Step {step} of 3</p>
          <button type="button" onClick={() => setOpen(false)} data-testid="wizard-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none px-2">×</button>
        </div>
        <h2 id="wizard-h2" className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {step === 1 && "What is your highest qualification?"}
          {step === 2 && "Which state are you applying from?"}
          {step === 3 && "Which sector interests you most?"}
        </h2>

        <div className="h-1 bg-slate-100 rounded-full mb-5 overflow-hidden">
          <div className="h-full bg-orange-500 transition-all" style={{ width: `${(step / 3) * 100}%` }} />
        </div>

        {step === 1 && (
          <div className="space-y-2" data-testid="wizard-step-qual">
            {QUALS.map((q) => (
              <button
                key={q.v}
                type="button"
                data-testid={`wizard-qual-${q.v}`}
                onClick={() => { setDraft({ ...draft, qualification: q.v }); setStep(2); }}
                className={`w-full text-left p-3 rounded-md border ${draft.qualification === q.v ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-orange-400 bg-white"} transition`}
              >
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-4 h-4 text-orange-600 shrink-0" aria-hidden="true" />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">{q.label}</div>
                    <div className="text-xs text-slate-500">{q.hint}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-state">
            <label className="block mb-3">
              <span className="text-xs font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Home state
              </span>
              <select
                value={draft.state}
                onChange={(e) => setDraft({ ...draft, state: e.target.value })}
                data-testid="wizard-state"
                className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
              >
                <option value="">All India (no state filter)</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <div className="flex items-center gap-3 mt-5">
              <button type="button" onClick={() => setStep(1)} className="text-sm text-slate-600 hover:text-slate-900 font-semibold">← Back</button>
              <button type="button" data-testid="wizard-state-next" onClick={() => setStep(3)} className="ml-auto px-4 py-2 rounded-md bg-orange-600 text-white text-sm font-bold hover:bg-orange-700">
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2" data-testid="wizard-step-sector">
            {SECTORS.map((s) => (
              <button
                key={s.v || "any"}
                type="button"
                data-testid={`wizard-sector-${s.v || "any"}`}
                onClick={() => finish({ ...draft, department: s.v })}
                className={`w-full text-left p-3 rounded-md border ${draft.department === s.v ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-orange-400 bg-white"} transition`}
              >
                <div className="flex items-center gap-3">
                  <Briefcase className="w-4 h-4 text-orange-600 shrink-0" aria-hidden="true" />
                  <span className="text-sm font-bold text-slate-900">{s.label}</span>
                </div>
              </button>
            ))}
            <button type="button" onClick={() => setStep(2)} className="text-sm text-slate-600 hover:text-slate-900 font-semibold mt-3">← Back</button>
          </div>
        )}
      </div>
    </div>
  );
}
